import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getMessaging, getToken, onMessage, deleteToken, type Messaging } from 'firebase/messaging'
import { app, FCM_VAPID_KEY } from '../firebase'
import api from '../api'
import { useUserStore } from './userStore'
import { useChatStore } from './chatStore'

// Local storage key — so we can unregister the old token on logout.
const TOKEN_STORAGE_KEY = 'riffdraft-fcm-token'

export const useNotificationStore = defineStore('notification', () => {
    // Browser-level permission state ('default' | 'granted' | 'denied').
    // The 'unsupported' sentinel means push is not available at all (e.g., iOS Safari
    // on an uninstalled PWA, or very old browsers).
    const permission = ref<NotificationPermission | 'unsupported'>('default')
    const fcmToken = ref<string | null>(null)
    const loading = ref(false)

    let messaging: Messaging | null = null
    let foregroundUnsubscribe: (() => void) | null = null

    const isSupported = computed(() => permission.value !== 'unsupported')
    const isEnabled = computed(() => permission.value === 'granted' && !!fcmToken.value)

    // Called once on app startup to detect browser support and current state.
    function init() {
        if (typeof window === 'undefined') return

        // Feature detection: need Notification API + Service Worker + Push API.
        // Safari on iOS only has these when running as an installed PWA.
        if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            permission.value = 'unsupported'
            return
        }

        permission.value = Notification.permission
        messaging = getMessaging(app)

        // If we already have a token from a previous session AND permission is
        // still granted, set up the foreground handler but don't prompt.
        if (permission.value === 'granted') {
            const stored = localStorage.getItem(TOKEN_STORAGE_KEY)
            if (stored) {
                fcmToken.value = stored
                setupForegroundHandler()
            }
        }
    }

    // Called when the user clicks the "Enable Notifications" toggle.
    // Must be triggered by a user gesture — browsers block permission prompts otherwise.
    async function enable(): Promise<boolean> {
        if (!messaging || permission.value === 'unsupported') return false

        loading.value = true
        try {
            // requestPermission returns immediately with the user's current choice
            // if already answered — otherwise shows the native prompt.
            const result = await Notification.requestPermission()
            permission.value = result

            if (result !== 'granted') {
                return false
            }

            // Wait for the service worker to be ready before requesting a token.
            // getToken will register its own sw if one isn't already active, but
            // we want ours (with the background message handler) to take ownership.
            // Timeout after 10s so a stuck SW doesn't hang the UI forever.
            const registration = await Promise.race([
                navigator.serviceWorker.ready,
                new Promise<ServiceWorkerRegistration>((_, reject) =>
                    setTimeout(() => reject(new Error('Service worker not ready after 10s — is the app running in dev mode without PWA devOptions enabled?')), 10000)
                )
            ])

            const token = await getToken(messaging, {
                vapidKey: FCM_VAPID_KEY,
                serviceWorkerRegistration: registration
            })

            if (!token) {
                console.error('FCM returned empty token')
                return false
            }

            fcmToken.value = token
            localStorage.setItem(TOKEN_STORAGE_KEY, token)

            // Send token to backend
            const userStore = useUserStore()
            if (!userStore.user) return false
            const authToken = await userStore.user.getIdToken()
            await api.post('/users/me/device-tokens',
                { token, platform: 'web' },
                { headers: { Authorization: `Bearer ${authToken}` } }
            )

            setupForegroundHandler()
            return true
        } catch (error) {
            console.error('Failed to enable notifications:', error)
            return false
        } finally {
            loading.value = false
        }
    }

    // Called when the user toggles notifications off. Deletes the FCM token
    // (so this browser stops receiving pushes) and removes it from the backend.
    async function disable() {
        if (!messaging) return

        loading.value = true
        try {
            const token = fcmToken.value
            if (token) {
                const userStore = useUserStore()
                if (userStore.user) {
                    try {
                        const authToken = await userStore.user.getIdToken()
                        await api.delete(`/users/me/device-tokens/${encodeURIComponent(token)}`, {
                            headers: { Authorization: `Bearer ${authToken}` }
                        })
                    } catch (err) {
                        console.error('Failed to unregister token on backend:', err)
                    }
                }
                try {
                    await deleteToken(messaging)
                } catch (err) {
                    console.error('Failed to delete FCM token locally:', err)
                }
            }

            localStorage.removeItem(TOKEN_STORAGE_KEY)
            fcmToken.value = null

            if (foregroundUnsubscribe) {
                foregroundUnsubscribe()
                foregroundUnsubscribe = null
            }
        } finally {
            loading.value = false
        }
    }

    // Listen for messages delivered while the app is in the foreground.
    // The browser doesn't fire native notifications automatically in this case,
    // so we construct one manually via the Notification API. This ensures the
    // user sees a banner whether or not the app is focused.
    function setupForegroundHandler() {
        if (!messaging || foregroundUnsubscribe) return
        foregroundUnsubscribe = onMessage(messaging, (payload) => {
            console.debug('[FCM foreground]', payload)
            const data = payload.data as Record<string, string> | undefined
            const title = payload.notification?.title || data?.title || 'Riff Draft'
            const body = payload.notification?.body || data?.body || ''

            // Suppress chat notifications when the user is actively viewing the
            // matching chat AND the tab is visible — they're already seeing the
            // message land via the polling loop.
            if (data?.type === 'chat' && document.visibilityState === 'visible') {
                const chatStore = useChatStore()
                const sameBand = chatStore.activeBandId != null && String(chatStore.activeBandId) === data.bandId
                const incomingSongId = data.songId ?? null
                const activeSongId = chatStore.activeSongId != null ? String(chatStore.activeSongId) : null
                const sameSong = incomingSongId === activeSongId
                if (sameBand && sameSong) {
                    console.debug('[FCM foreground] suppressed — user is in this chat')
                    return
                }
            }

            try {
                // Show the notification via the service worker registration so it
                // behaves identically to background-delivered ones (including the
                // notificationclick handler we set up in sw.ts).
                if (Notification.permission === 'granted' && 'serviceWorker' in navigator) {
                    navigator.serviceWorker.ready.then(reg => {
                        reg.showNotification(title, {
                            body,
                            icon: '/pwa-192x192.png',
                            badge: '/pwa-64x64.png',
                            tag: (payload.data as Record<string, string> | undefined)?.type || 'riffdraft',
                            data: payload.data
                        })
                    }).catch(err => console.error('Failed to show foreground notification:', err))
                }
            } catch (err) {
                console.error('Failed to show foreground notification:', err)
            }
        })
    }

    // Called on logout. We want to unregister this device's token so the
    // user stops getting pushes on a shared computer.
    async function clear() {
        await disable()
    }

    return {
        permission,
        fcmToken,
        loading,
        isSupported,
        isEnabled,
        init,
        enable,
        disable,
        clear
    }
})
